import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getOfferings } from '../../ManageOffering/services/OfferingService';
import Service from '../services/Service';
import showToast from '../../../../Shared/Utils/ToastNotification';
import './SuggestMedicationModal.css';

const SuggestMedicationModal = ({ patient, onClose }) => {
  const [offerings, setOfferings] = useState([]);
  const [selectedOfferings, setSelectedOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOfferings();
  }, []);

  const fetchOfferings = async () => {
    try {
      const data = await getOfferings();
      setOfferings(data);
    } catch (error) {
      showToast('error', 'Failed to fetch offerings');
    } finally {
      setLoading(false);
    }
  };

  const toggleOffering = (offering) => {
    setSelectedOfferings((prev) => (prev.find((o) => o.id === offering.id) ? prev.filter((o) => o.id !== offering.id) : [...prev, offering]));
  };

  const handleSuggest = async () => {
    if (selectedOfferings.length === 0) {
      showToast('error', 'Please select at least one offering');
      return;
    }

    const invalidOffering = selectedOfferings.find((o) => !o.price || parseFloat(o.price) === 0);
    if (invalidOffering) {
      showToast('error', `Please set price for "${invalidOffering.title}" minimum $1`);
      return;
    }

    setSubmitting(true);
    try {
      await Service.suggestMedication(patient.id, {
        medications: selectedOfferings.map((o) => ({ offering_id: o.id, title: o.title })),
      });
      showToast('success', 'Medication suggested successfully');
      onClose();
    } catch (error) {
      showToast('error', error.response?.data?.message || 'Failed to suggest medication');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Suggest Medication</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="patient-info">
            Patient:{' '}
            <strong>
              {patient.first_name} {patient.last_name}
            </strong>{' '}
            ({patient.email})
          </p>
          {loading ? (
            <p>Loading offerings...</p>
          ) : (
            <div className="offerings-list">
              {offerings.map((offering) => (
                <div
                  key={offering.id}
                  className={`offering-item ${selectedOfferings.find((o) => o.id === offering.id) ? 'selected' : ''}`}
                  onClick={() => toggleOffering(offering)}
                >
                  <input type="checkbox" checked={!!selectedOfferings.find((o) => o.id === offering.id)} onChange={() => toggleOffering(offering)} />
                  <label>
                    {offering.title} - ${offering.price || '0'}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-danger" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSuggest} disabled={submitting || selectedOfferings.length === 0}>
            {submitting ? 'Suggesting...' : 'Suggest'}
          </button>
        </div>
      </div>
    </div>
  );
};

SuggestMedicationModal.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SuggestMedicationModal;
